<template>
	<div class="dropdown-field" :id="myClass" ref="dropdown_field_ref">
		<default-widget-field v-bind="defaultFieldProps" v-on="defaultFieldListeners" />
		<div>
			<DropdownWithCustomOptionsField
				v-if="isUserCustomOptionsSelectionEnabled"
				v-model="value"
				:ref="fieldRef"
				class="widget-input-field"
				persistent-placeholder
				:dropdownOptions="dropdownOptions"
				:isMultipleSelectionEnabled="isMultipleSelection"
				:placeholder="fieldMetadata.options.placeholder.value"
				:activeFormFieldCommonProps="activeFormFieldCommonProps"
			/>
			<v-autocomplete
				:ref="fieldRef"
				v-else-if="isDropdownAC"
				v-model="value"
				data-lpignore="true"
				class="widget-input-field"
				persistent-placeholder
				:items="dropdownOptions"
				:chips="isMultipleSelection"
				:multiple="isMultipleSelection"
				v-bind="activeFormFieldCommonProps"
				:return-object="isMultipleSelection"
				:placeholder="fieldMetadata.options.placeholder.value"
			/>
			<v-select
				v-else
				v-model="value"
				:ref="fieldRef"
				data-lpignore="true"
				class="widget-input-field"
				persistent-placeholder
				v-bind="activeFormFieldCommonProps"
				:items="dropdownOptions"
				:menu-props="{ contentClass: 'dropdown-field-menu', 'nudge-top': nudgeTop, top: isLocateMenuOnTop }"
				:chips="isMultipleSelection"
				:multiple="isMultipleSelection"
				:return-object="isMultipleSelection"
				:placeholder="fieldMetadata.options.placeholder.value"
				:attach="attachmentElement"
				v-on:focus="setScrollableForm(false)"
				v-on:blur="setScrollableForm(true)"
			>
				<!-- :attach="#${this.myClass}" -->
				<template #selection="{ item }">
					<v-chip v-if="isMultipleSelection" outlined close @click:close="close(item)">{{ item.text }}</v-chip>
					<span v-else>{{ item.text }} </span>
				</template>
				<template #item="{ item }">
					<div class="flex-centerize">
						<div class="v-list-item__action" v-if="isMultipleSelection">
							<div class="v-simple-checkbox">
								<div class="v-input--selection-controls__input">
									<i
										aria-hidden="true"
										class="v-icon notranslate theme--light"
										:class="{ 'far fa-square': !isItemSelected(item), 'fas fa-check-square primary--text': isItemSelected(item) }"
									></i>
								</div>
							</div>
						</div>
						<div class="v-list-item__content">
							<div class="v-list-item__title">{{ item.text }}</div>
						</div>
					</div>
				</template>
			</v-select>
		</div>
	</div>
</template>

<script>
import { v4 as uuid } from 'uuid';

import DropdownWithCustomOptionsField from './DropdownWithCustomOptionsField';
import DefaultWidgetField from '../DefaultWidgetField';
import WidgetFieldMixin from '../WidgetFieldMixin';
import WidgetFieldTableOptionsMixin from '../WidgetFieldTableOptionsMixin';
import WidgetFieldService from '@/components/BlockEditorWidget/WidgetFieldService';
import WidgetFormService from '@/components/WidgetForm/WidgetFormService';

export default {
	name: 'DropdownField',
	components: {
		DefaultWidgetField,
		DropdownWithCustomOptionsField,
	},
	mixins: [WidgetFieldMixin, WidgetFieldTableOptionsMixin],
	props: {
		previousSteps: { type: Object },
		replaceInsertDataStringDict: { type: Object },
		configurationsMappingBlocksUUIDs: { type: Array },
		isFullHeightForm: { type: Boolean },
		widgetFormScrollHeight: { type: Number },
	},
	computed: {
		placeholderValueLocal: {
			set(value) {
				this.$emit('update:placeholderValue', value);
			},
			get() {
				if (!this.placeholderEnabled) {
					return '';
				}
				return this.placeholderValue;
			},
		},
		placeholder() {
			if (!this.fieldOptions.placeholder.enabled) {
				return '';
			}
			return this.placeholderValue;
		},
		isMultipleSelection() {
			return WidgetFieldService.isMultipleSelection(this.field);
		},
		isUserCustomOptionsSelectionEnabled() {
			return this.fieldOptions.user_custom_options?.enabled;
		},
		dropdownOptions() {
			const { dropdown_options: manualOptions } = this.fieldOptions;
			const options = this.isTableOptionsMode ? this.dynamicTableOptions : manualOptions;
			return this.onlyTextOption(options);
		},
		optionValueMode() {
			return this.fieldOptions.option_value?.enabled;
		},
		preselect() {
			return this.fieldOptions.preselect;
		},
		preselectEnabled() {
			return this.preselect.enabled;
		},
		preselectValue() {
			return this.preselect.value;
		},
		isDropdownAC() {
			return this.fieldOptions.autocomplete ? this.fieldOptions.autocomplete.enabled : false;
		},
		customFieldProps() {
			return { dense: true };
		},
		isLocateMenuOnTop() {
			const { isFullHeightForm, widgetFormScrollHeight, topLocation } = this;
			return isFullHeightForm && widgetFormScrollHeight - topLocation < 400 + this.dropdownOptions.length * 30;
		},
		attachmentElement() {
			// We either 1) attach to the body (false) if the form is small (so menu is not cut by the overflow: auto)
			// 2) Attach to the dropdown element so it scrolls along with form when it scrolls
			return this.isFullHeightForm ? `#${this.myClass}` : false;
		},
		nudgeTop() {
			if (this.isFullHeightForm) {
				return this.isLocateMenuOnTop ? 0 : -65;
			}
			return -37;
		},
	},
	methods: {
		setScrollableForm(isScrollable) {
			this.$emit('field-event', { action: 'setScrollable', value: isScrollable });
		},
		onlyTextOption(options) {
			return options.map((o) => ({
				...o,
				value: this.translatePreviousStepsValue(this.optionValueMode ? o.value : o.text),
				text: this.translatePreviousStepsValue(o.text),
			}));
		},
		translatePreviousStepsValue(value) {
			return this.previousSteps
				? WidgetFormService.processPreviousSteps(
						value,
						this.previousSteps,
						this.replaceInsertDataStringDict,
						this.configurationsMappingBlocksUUIDs
				  )
				: value;
		},
		close(item) {
			this.value = this.value.filter((v) => v.id !== item.id);
		},
		isItemSelected(item) {
			const value = this.getValueAfterChangeBetweenSingleAndMultiSelect();
			return value.map((v) => v.id).includes(item.id);
		},
		getValueAfterChangeBetweenSingleAndMultiSelect() {
			// support for switching between sing & multi select
			if (this.isMultipleSelection && !Array.isArray(this.value)) {
				return [this.dropdownOptions.find((v) => v.value === this.value)];
			} else if (!this.isMultipleSelection && Array.isArray(this.value)) {
				return this.value[0];
			}
			return this.value;
		},
		getValuePreselectSingleAndMultiSelect() {
			if (this.isMultipleSelection) {
				return this.dropdownOptions.filter((option) =>
					Array.isArray(this.preselectValue) ? this.preselectValue.includes(option.value) : option.value === this.preselectValue
				);
			}
			return this.dropdownOptions.find((option) => option.value === this.preselectValue)?.value;
		},
	},
	mounted() {
		if (this.preselectEnabled && (!this.value || (Array.isArray(this.value) && this.value.length === 0))) {
			this.value = this.getValuePreselectSingleAndMultiSelect();
		} else {
			this.value = this.getValueAfterChangeBetweenSingleAndMultiSelect();
		}
		this.myClass = 'dropdown-field-' + uuid();
		this.topLocation = this.$refs.dropdown_field_ref.getBoundingClientRect().top;
	},
	data() {
		return { myClass: '', topLocation: 0 };
	},
};
</script>

<style scoped lang="scss">
.dropdown-field {
	position: relative;
}

.edit-mode {
	.v-icon {
		font-size: 30px !important;
	}
}

.widget-input-field {
	::v-deep(.v-chip) {
		height: 28px;
		border-radius: 2px !important;

		&.v-chip--outlined {
			background: #f4f4f5 !important;
			border-color: #f4f4f5;
		}

		.v-icon::before {
			padding: 0 0 4px 3px;
			content: '\00d7'; // X icon
			font-weight: 100;
			font-size: 20px;
			color: #6a6b6f;
		}
	}
	::v-deep(.v-select__slot) {
		overflow-y: hidden;
		margin-top: 1px;
		margin-bottom: 1px;

		input {
			padding: 0 10px !important;
		}

		.v-input__append-inner {
			margin-top: 4px !important;
		}
	}
	::v-deep(.v-input__slot) {
		position: relative;
		padding: 0 4px !important;

		fieldset {
			min-height: 39px !important;
		}
	}
	::v-deep(.v-select__selections) {
		position: absolute;
		min-height: 32px !important;
		margin-top: 1px;
		margin-bottom: 1px;
		padding: 2px !important;
		top: 0;
		height: 100%;
		width: 100%;
		overflow-y: auto;

		.v-chip {
			height: 24px;
			margin: 2px;
		}
	}
}
</style>

<style lang="scss">
// This is generaly on the app which is far from idel and an opening for bugs
// but at least this is name spaced with 'dropdown-field-menu'
.dropdown-field-menu.v-menu__content {
	.v-list {
		padding: 4px 0;
	}

	.v-list-item__action {
		margin: 8px 5px 8px 0 !important;
	}
	.v-input--selection-controls__input {
		height: auto;
	}
	.v-list-item {
		padding: 0 11px;

		.primary--text {
			color: #459ffa !important;
		}
	}
	.v-list-item__title {
		color: #4d4c52 !important;
	}
	.v-list-item--active {
		.v-list-item__title {
			color: #459ffa !important;
		}
		.v-list-item--link:before {
			background-color: #d9ecfe;
		}
	}

	.theme--light.v-list-item:not(:hover)::before {
		opacity: 0;
	}

	.v-icon {
		$size: 18px;
		font-size: $size;
		height: $size;
		width: $size;
	}
	.fa-square::before {
		color: #dcdfe5;
	}
}
</style>
